import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Command,
  CommandInput,
  CommandList,
  Kbd,
  Spinner,
} from "@salestrak/ui";

import { FeedbackDialog } from "#app/components/navigation/feedback-dialog";
import { useGlobalSearchContent } from "./use-global-search-content";
import { GlobalSearchFooter } from "./global-search-footer";

// The ⌘ glyph only reads right on Mac; elsewhere spell the modifier out. The
// shortcut itself fires on metaKey OR ctrlKey, so both labels are accurate.
const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
const SEARCH_SHORTCUT = IS_MAC ? "⌘K" : "Ctrl K";

/**
 * Desktop inline global search. Not an overlay — the header input IS the top of
 * a single container that expands downward in place once focused (scope chips,
 * results, and footer flow beneath the input inside the same bordered card),
 * rather than floating a detached popover/dialog beside it.
 *
 * The expanded body is an absolutely-positioned continuation of the input row,
 * so growing it overlays the page below without shifting header layout. The
 * header wrapper (`app-shell`) carries a z-index so this stack paints above page
 * content; a document-level pointerdown handler closes it on an outside click.
 * `Command` wraps the whole thing so the highlight + keyboard nav span the input
 * and the list. The mobile breakpoint uses the `GlobalSearchDialog` drawer
 * instead — this only mounts in the desktop header center slot.
 */
// The collapsed bar sits at COLLAPSED_WIDTH; on open the whole card widens to
// OPEN_WIDTH, centered on the collapsed footprint (the negative left offset
// absorbs half the extra width). Collapsed, the bar is a 32px pill — the
// named top-bar control height the workspace selector and user menu share;
// open, the input row relaxes to INPUT_HEIGHT for the panel.
const COLLAPSED_WIDTH = 480;
const OPEN_WIDTH = 800;
const COLLAPSED_HEIGHT = 32;
const INPUT_HEIGHT = 42;
const LIST_MAX_HEIGHT = 400;

export function GlobalSearchBar() {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    inputRef.current?.blur();
  }, []);

  // Return focus to the input after a control inside the panel (e.g. a chip)
  // takes it — arrow-key navigation is bound to the input. rAF lets the click's
  // own focus settle first so we win the race.
  const focusInput = useCallback(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const {
    query,
    setQuery,
    loading,
    itemsContent,
    scopeBar,
    footerContext,
    onItemHighlighted,
    listRef,
    feedbackOpen,
    setFeedbackOpen,
  } = useGlobalSearchContent({ onClose: close, focusInput });

  // Closing (escape, outside click, or selecting an item) resets the query so
  // the next open starts clean. Covers every close path in one place.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open, setQuery]);

  // ⌘K / Ctrl+K focuses the inline bar — the single desktop search surface.
  // This component only renders in the desktop header center slot, so the
  // listener is effectively desktop-only; mobile keeps useCtrlKShortcut.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Compare lowercased — with CapsLock on, e.key is "K".
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // No portal/overlay backs this panel, so close it ourselves when a pointer
  // lands anywhere outside the container. Capture phase so it runs before the
  // target's own handlers; clicks inside (input, chips, rows) are left alone.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = containerRef.current;
      if (el && e.target instanceof Node && !el.contains(e.target)) close();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, close]);

  return (
    <>
      {/* The outermost slot child carries the collapsed footprint so the
          Header's center slot centers it — the same contract the old search
          button had. It is `relative` and reserves the collapsed input's height
          so the absolutely-positioned panel can grow beneath it without moving
          the header; the z-index lifts the expanded panel above sibling header
          controls. */}
      <Box
        width={COLLAPSED_WIDTH}
        position="relative"
        height={COLLAPSED_HEIGHT}
        zIndex={open ? 50 : undefined}
      >
        <Command
          autoHighlight={false}
          mode="none"
          onItemHighlighted={onItemHighlighted}
          onValueChange={setQuery}
          value={query}
        >
          {/* Closed: fills the slot (left/right 0). Open: a fixed 800px card
              centered on the slot, so it grows symmetrically instead of
              shoving right. */}
          <Box
            ref={containerRef}
            position="absolute"
            top={0}
            left={open ? (COLLAPSED_WIDTH - OPEN_WIDTH) / 2 : 0}
            right={open ? undefined : 0}
            width={open ? OPEN_WIDTH : undefined}
            direction="column"
            background={open ? "popover" : "card"}
            border
            rounded={open ? "md" : "full"}
            overflow="hidden"
            className={open ? "shadow-2xl" : "hover:border-primary"}
            aria-label="Search everything"
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Escape") close();
            }}
          >
            {/* The input row — the permanent top of the panel. A bottom border
                becomes the divider under it once the body expands. */}
            <Box
              direction="row"
              align="center"
              gap="xs"
              paddingRight="sm"
              height={open ? INPUT_HEIGHT : COLLAPSED_HEIGHT}
              border={open ? "bottom" : undefined}
            >
              {/* CommandInput carries ~20px of built-in start inset (wrapper
                  padding + the addon's own); pulling it 12px left seats the
                  search icon 8px from the pill edge — the same distance the
                  shortcut chip keeps on the right. */}
              <Box grow minWidth={0} className="-ms-3">
                <CommandInput
                  ref={inputRef}
                  autoFocus={false}
                  onFocus={() => setOpen(true)}
                  placeholder="Search"
                />
              </Box>
              {loading ? (
                <Spinner color="muted" size="md" />
              ) : (
                <Kbd>{SEARCH_SHORTCUT}</Kbd>
              )}
            </Box>

            {open && (
              <>
                {scopeBar}
                {/* Bounded + min-h-0 so the library ScrollArea inside
                    CommandList gets a definite height and actually scrolls. */}
                <Box direction="column" maxHeight={LIST_MAX_HEIGHT}>
                  <CommandList ref={listRef}>{itemsContent}</CommandList>
                </Box>
                <GlobalSearchFooter context={footerContext} />
              </>
            )}
          </Box>
        </Command>
      </Box>

      {feedbackOpen && (
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      )}
    </>
  );
}
